// ============================================================
// FINANCIAL SETTLEMENTS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { DollarSign, Download, CheckCircle } from 'lucide-react';
import { Table, Badge, Button, Modal, StatCard } from '../components/ui';
import { getSettlements } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency, formatDate, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';

const FinancialSettlements = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);

  useEffect(() => {
    adminApi.getSettlements(getSettlements)
      .then(res => { setData(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const markPaid = async (id) => {
    try { await adminApi.markSettlementPaid(id); } catch { /* fallback */ }
    setData(p => p.map(r => r.id === id ? { ...r, status: 'completed', paidAt: new Date().toISOString() } : r));
    toast.success('Settlement marked as paid');
  };

  const totalPending = data.filter(r => r.status === 'pending').reduce((s, r) => s + r.amount, 0);
  const totalCompleted = data.filter(r => r.status === 'completed').reduce((s, r) => s + r.amount, 0);

  const columns = [
    { key: 'settlementId', label: 'Settlement ID', render: v => <code>#{v}</code> },
    { key: 'entityName', label: 'Entity', render: v => <b>{v}</b> },
    { key: 'entityType', label: 'Type', render: v => <Badge status={v} text={v} /> },
    { key: 'amount', label: 'Amount', render: v => <b style={{ color: '#10B981' }}>{formatCurrency(v)}</b> },
    { key: 'deductions', label: 'Deductions', render: v => formatCurrency(v) },
    { key: 'netAmount', label: 'Net Payable', render: v => <b>{formatCurrency(v)}</b> },
    { key: 'status', label: 'Status', render: v => <Badge status={v === 'completed' ? 'active' : v === 'pending' ? 'pending' : 'warning'} text={v} /> },
    { key: 'period', label: 'Period' },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => setView(row)}>View</Button>
          {row.status === 'pending' && (
            <Button size="sm" variant="success" icon={<CheckCircle size={12} />} onClick={() => markPaid(row.id)}>Mark Paid</Button>
          )}
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Financial Settlements</h1>
          <p className="admin-page-subtitle">Manage payouts to restaurants and delivery partners</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />}
          onClick={() => downloadCSV(data.map(({ id, ...r }) => r), 'settlements.csv')}>
          Export CSV
        </Button>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Records" value={data.length} icon={<DollarSign size={18} />} color="#3B82F6" />
        <StatCard label="Pending Amount" value={formatCurrency(totalPending)} icon={<DollarSign size={18} />} color="#F59E0B" />
        <StatCard label="Settled Amount" value={formatCurrency(totalCompleted)} icon={<DollarSign size={18} />} color="#10B981" />
        <StatCard label="Pending Count" value={data.filter(r => r.status === 'pending').length} icon={<DollarSign size={18} />} color="#EF4444" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <span style={{ fontSize: 14, fontWeight: 700 }}>{data.length} Settlements</span>
        </div>
        <Table columns={columns} data={data} loading={loading} />
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title="Settlement Details"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            {view?.status === 'pending' && <Button variant="success" icon={<CheckCircle size={14} />} onClick={() => { markPaid(view.id); setView(null); }}>Mark as Paid</Button>}
            <Button variant="secondary" onClick={() => setView(null)}>Close</Button>
          </div>
        }>
        {view && (
          <div className="admin-info-grid">
            {[
              ['Settlement ID', `#${view.settlementId}`], ['Entity', view.entityName],
              ['Entity Type', view.entityType], ['Period', view.period],
              ['Gross Amount', formatCurrency(view.amount)], ['Deductions', formatCurrency(view.deductions)],
              ['Net Payable', formatCurrency(view.netAmount)], ['Status', view.status],
              ['Bank', view.bankName || '—'], ['Account', view.accountNumber || '—'],
              ['Created', formatDate(view.createdAt)], ['Paid On', view.paidAt ? formatDate(view.paidAt) : 'Not paid yet'],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-item">
                <span className="admin-info-label">{l}</span>
                <span className="admin-info-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FinancialSettlements;
